// src/hooks/pos/useLockSessionStatus.js
//
// F12a-N task-n8 — lightweight session-status lookup that feeds the
// OperatorLockScreen status chip (CashierStation + WaiterStation both use
// this, see those files' `sessionStatus` wiring).
//
// Deliberately NOT the same instance as useSessionManager's `activeSession`:
// that hook is employeeId-gated and only lives inside SellSurface, which
// only mounts AFTER the first operator unlock — but the lock screen must
// show session status even at first boot, before anyone has ever unlocked
// (the device itself is tenant-authed independently of any operator PIN, so
// the read is valid pre-unlock too). Lifting SellSurface's session state up
// into the stations would need a new context bridge (the SellExtrasProvider
// pattern TopBarExtras already uses) — WaiterStation has no equivalent
// bridge at all (it hosts TableServicePage directly, no SellSurface-style
// wrapper), so a shared bridge isn't practical for both stations without a
// much larger refactor. This hook is the pragmatic alternative: one small,
// independent, read-only fetch, shared as a hook (not duplicated per
// station) so the matching semantics can't drift between Cashier and
// Waiter.
//
// Matching semantics: useSessionManager matches per-employee when
// eodMode === 'per_employee'. This hook has no signed-in operator to match
// against while locked, so it deliberately does NOT replicate that —
// instead it uses the same store-level fallback useSessionManager itself
// already uses for eodMode === 'per_store': ANY open session counts,
// surfaced as "open" with that session's id/start time. Pragmatic, not a
// per-employee "is THIS operator's session open" check.
import { useCallback, useEffect, useState } from 'react';
import { sessionService } from '../../services/api';

export default function useLockSessionStatus(locked) {
    // undefined = loading/unknown -> caller renders nothing (no spinner
    // clutter). Resolves to { open: true, sessionId, startTime } or
    // { open: false }.
    const [status, setStatus] = useState(undefined);

    const refresh = useCallback(async () => {
        try {
            const sessions = await sessionService.getActiveSessions();
            const open = Array.isArray(sessions) && sessions.length > 0 ? sessions[0] : null;
            setStatus(open
                ? { open: true, sessionId: open.session_id, startTime: open.start_time }
                : { open: false });
        } catch (err) {
            console.error('Failed to load session status for lock screen:', err);
            // Leave status as-is — a transient network blip shouldn't flip an
            // already-known-good chip back to the loading/blank state.
        }
    }, []);

    useEffect(() => {
        if (!locked) return;
        // Fresh load on every lock (including a re-lock straight after EOD) —
        // never show a stale chip carried over from the previous episode.
        setStatus(undefined);
        refresh();
    }, [locked, refresh]);

    return status;
}
